import moment from 'moment'
import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'
import { fareClassToUrlMap, type FlightsClassTypes } from 'constants/flight'
import { urlHashPackageOptions } from 'constants/url'
import type { DateRange } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/types'
import { allocatePackagesForDate, getJourneyTotalFareForFareClass, getPromotionSeatsLeftFromJourney, hasSufficientSeats, isRoundTripFnfPriceApplied } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/utils'
import { charterCalendarDayKey } from 'lib/maldivesCharter/charterCalendarDayKey'
import { isMaldivesCharterJourney } from 'lib/maldivesCharter/isMaldivesCharterJourney'
import { getCharterFallbackDateGroups, isCharterSearchWithinWindow, rankCharterRecommendations } from 'lib/maldivesCharter/charterRecommendationDates'
import offerPageURL from 'lib/offer/offerPageURL'
import { encodeChildrenOccupancy } from 'lib/search/searchUtils'
import { getRoundTripSeatsLeft } from './charterFlightAvailability'

export const CHARTER_PREVIEW_CHECK_IN = 'charterPreviewCheckIn'
export const CHARTER_PREVIEW_CHECK_OUT = 'charterPreviewCheckOut'

export interface CharterBundleRecommendation extends DateRange {
  flightPrice: number
  departingJourney: App.JourneyV2
  returningJourney: App.JourneyV2
}

interface CandidateOptions {
  offer: App.HotelOffer
  rooms: Array<App.Occupants>
  fares: Array<Array<App.JourneyV2>>
  calendarDataByOccupancy: Record<string, Record<string, App.Calendar>>
  fareClass: FlightsClassTypes
  seatCount: number
  today: string
}

function getBundleFare(journey: App.JourneyV2, fareClass: FlightsClassTypes, seatCount: number, allowFnf: boolean): number | undefined {
  const promotionAvailable = !journey.promotionPrice || hasSufficientSeats(getPromotionSeatsLeftFromJourney(journey), seatCount)
  const pricedJourney = {
    ...journey,
    fnfEligible: allowFnf && journey.fnfEligible,
    promotionPrice: promotionAvailable ? journey.promotionPrice : undefined,
  }
  if (pricedJourney.price?.all?.totalFare === undefined) return undefined
  return getJourneyTotalFareForFareClass({ journey: pricedJourney, fareClass, seatEligiblePassengers: seatCount })
}

/** Only provider-paired return flights with a purchasable hotel stay can be recommended. */
export function getCharterBundleCandidates({ offer, rooms, fares, calendarDataByOccupancy, fareClass, seatCount, today }: CandidateOptions): Array<CharterBundleRecommendation> {
  const returningFares = new Map<string, App.JourneyV2>()
  for (const fare of fares[1] ?? []) {
    returningFares.set(fare.id, fare)
    returningFares.set(fare.bookingInfo.boookingKey, fare)
  }
  const candidates: Array<CharterBundleRecommendation> = []
  for (const departing of fares[0] ?? []) {
    const departureDate = charterCalendarDayKey(departing.flightGroup.flights[0]?.departingDate)
    const checkIn = charterCalendarDayKey(departing.flightGroup.flights.at(-1)?.arrivalDate)
    if (!departureDate || departureDate < today || !checkIn || !isMaldivesCharterJourney(departing) ||
      departing.flightGroup.flights[0]?.departureAirport !== 'MEL') continue
    for (const pairedId of departing.pairedFareId) {
      const returning = returningFares.get(pairedId)
      if (!returning || !isMaldivesCharterJourney(returning) || returning.flightGroup.flights[0]?.departureAirport !== 'MLE') continue
      const checkOut = charterCalendarDayKey(returning.flightGroup.flights[0]?.departingDate)
      const returnDate = charterCalendarDayKey(returning.flightGroup.flights.at(-1)?.arrivalDate)
      if (!checkOut || !returnDate || checkOut <= checkIn || !hasSufficientSeats(getRoundTripSeatsLeft(departing, returning), seatCount)) continue
      const nights = moment.utc(checkOut).diff(moment.utc(checkIn), 'days')
      if (!CHARTER_FLIGHT_DETAILS['melbourne-maldives'].validDurations.includes(nights)) continue
      const allowFnf = isRoundTripFnfPriceApplied(departing, returning, seatCount)
      const departingPrice = getBundleFare(departing, fareClass, seatCount, allowFnf)
      if (departingPrice === undefined || !Number.isFinite(departingPrice) || departingPrice < 0) continue
      const returningPrice = getBundleFare(returning, fareClass, seatCount, allowFnf)
      if (returningPrice === undefined || !Number.isFinite(returningPrice) || returningPrice < 0 || departingPrice + returningPrice <= 0) continue
      const allocation = allocatePackagesForDate({
        offer, rooms, durationPackages: offer.packages.filter(pkg => pkg.duration === nights), calendarDataByOccupancy, checkIn,
      })
      if (!allocation.isValid) continue
      candidates.push({
        departureDate,
        returnDate,
        checkIn,
        checkOut,
        nights,
        durationDays: moment.utc(returnDate).diff(moment.utc(departureDate), 'days'),
        flightPrice: departingPrice + returningPrice,
        departingJourney: departing,
        returningJourney: returning,
      })
    }
  }
  return candidates
}

export function getCharterBundleRecommendation(candidates: Array<CharterBundleRecommendation>, duration: number, checkIn?: string, checkOut?: string): CharterBundleRecommendation | undefined {
  if (checkIn && checkOut && candidates.some(candidate => candidate.checkIn === checkIn && candidate.checkOut === checkOut)) return undefined
  // Shared policies operate on stay dates here, and on flight dates for standalone.
  const options = candidates.map(candidate => ({
    departureDate: candidate.checkIn,
    returnDate: candidate.checkOut,
    nights: candidate.nights,
    totalPrice: candidate.flightPrice,
    candidate,
  }))
  if (!checkIn || !checkOut) {
    const matching = options.filter(option => option.nights === duration)
    return rankCharterRecommendations(matching.length ? matching : options)[0]?.candidate
  }
  const nearby = options.filter(option => isCharterSearchWithinWindow(option, checkIn, checkOut))
  const groups = getCharterFallbackDateGroups(options, checkIn, checkOut)
  const fallback = groups.flatMap(group => [group.matchingDuration, group.bundleDuration]).find(group => group.length)
  return rankCharterRecommendations(nearby.length ? nearby : (fallback ?? []), checkIn, checkOut)[0]?.candidate
}

export function getCharterBundleRecommendationUrl(offer: App.HotelOffer, recommendation: CharterBundleRecommendation, rooms: Array<App.Occupants>, fareClass: FlightsClassTypes, origin: string | undefined, hasSelectedDates: boolean): string {
  const params = new URLSearchParams({
    adults: rooms.map(room => room.adults).join(','),
    children: rooms.map(room => encodeChildrenOccupancy(room.childrenAge)).join(','),
    duration: String(recommendation.nights),
    fareClass: fareClassToUrlMap[fareClass],
    selectedCabin: fareClassToUrlMap[fareClass],
  })
  if (origin) params.set('departureAirport', origin)
  params.set(hasSelectedDates ? 'checkIn' : CHARTER_PREVIEW_CHECK_IN, recommendation.checkIn)
  params.set(hasSelectedDates ? 'checkOut' : CHARTER_PREVIEW_CHECK_OUT, recommendation.checkOut)
  return offerPageURL(offer, params, urlHashPackageOptions)
}
